import React from 'react'

// How a shared practice set is shown to a teacher. Two kinds exist:
//   Special Mode's noun-case drill — sentence, noun, case, and slips per part
//   Verb Lab                       — verb, tense, person, and what was tried
// Verb Lab items carry `kind: 'verb'`. Anything else is a case item, which is
// how every set shared before Verb Lab could share was written.
//
// Colours come from the surrounding view's theme variables, with the dark
// palette as the fallback, so the same list reads correctly in the teacher's
// light dashboard and in the dark Homework dialog.

export const parseDrillDetails = (json) => {
  try { const a = JSON.parse(json || '[]'); return Array.isArray(a) ? a : [] } catch { return [] }
}

const MODE_LABELS = { 'verb-lab': 'Verb Lab', 'en-tenses': 'English tenses', 'en-articles': 'English articles' }
export const drillModeLabel = (mode) => MODE_LABELS[mode] || mode || ''

// [before, answer, after]: the answer located inside its sentence, so it can be
// highlighted. Case-insensitive; when it isn't found the sentence comes back whole.
export const splitAround = (sentence, answer) => {
  const s = String(sentence || '')
  const a = String(answer || '')
  const i = a ? s.toLowerCase().indexOf(a.toLowerCase()) : -1
  return i < 0 ? [s, '', ''] : [s.slice(0, i), s.slice(i, i + a.length), s.slice(i + a.length)]
}

const S = {
  list: { display: 'flex', flexDirection: 'column', gap: '0.45rem' },
  row: { background: 'var(--chip-bg, var(--frost-field))', border: '1px solid var(--border, var(--frost-border))', borderRadius: 10, padding: '0.5rem 0.7rem' },
  text: { color: 'var(--text, var(--frost-text))', lineHeight: 1.4 },
  meta: { color: 'var(--muted, var(--frost-muted))', fontSize: '0.78rem', marginTop: '0.2rem', lineHeight: 1.45 },
  answer: { color: 'var(--accent, var(--frost-right))', fontWeight: 600 },
  tried: { color: 'var(--amber, var(--frost-amber))' },
  clean: { color: 'var(--accent, var(--frost-right))', fontSize: '0.85rem' },
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

function VerbMiss({ item }) {
  const [before, hit, after] = splitAround(item.sentence, item.answer)
  const tried = (Array.isArray(item.tried) ? item.tried : []).filter(Boolean)
  const who = [item.person, item.pronoun].filter(Boolean).join(' — ')
  return (
    <div style={S.row}>
      <div style={S.text}>{before}<span style={S.answer}>{hit}</span>{after}</div>
      <div style={S.meta}>
        {item.verb}{item.meaning ? ` (${item.meaning})` : ''}{item.tense ? ` · ${item.tense}` : ''}{who ? ` · ${who}` : ''}
        {' → '}<span style={S.answer}>{item.answer}</span>
        {tried.length > 0 && <> · tried <span style={S.tried}>{tried.join(', ')}</span></>}
        {item.attempts > 1 ? ` · ${plural(item.attempts, 'attempt')}` : ''}
        {item.hinted ? ' · used hints' : ''}{item.revealed ? ' · revealed' : ''}{item.skipped ? ' · skipped' : ''}
      </div>
    </div>
  )
}

function CaseMiss({ item }) {
  return (
    <div style={S.row}>
      <div style={S.text}>{item.sentence}</div>
      <div style={S.meta}>
        {item.noun} → <span style={S.answer}>{item.answer}</span> · {item.case}
        {item.caseAttempts ? ` · ${plural(item.caseAttempts, 'case slip')}` : ''}
        {item.endAttempts ? ` · ${plural(item.endAttempts, 'ending slip')}` : ''}
        {item.hinted ? ' · used hints' : ''}{item.revealed ? ' · revealed' : ''}{item.skipped ? ' · skipped' : ''}
      </div>
    </div>
  )
}

// A pick-one item (English articles): the sentence with the right choice in
// place, what was picked instead, and the rule that decides it. An empty choice
// — no article at all — shows as ∅ so the gap is visible rather than invisible.
const choiceText = (a) => (!a || a === '—' ? '∅' : a)
function ChoiceMiss({ item }) {
  const tried = (Array.isArray(item.tried) ? item.tried : []).filter(t => t != null)
  return (
    <div style={S.row}>
      <div style={S.text}>{item.before}{' '}<span style={S.answer}>{choiceText(item.answer)}</span>{' '}{item.after}</div>
      <div style={S.meta}>
        {item.rule || ''}
        {tried.length > 0 && <>{item.rule ? ' · ' : ''}picked <span style={S.tried}>{tried.map(choiceText).join(', ')}</span></>}
        {item.revealed ? ' · revealed' : ''}{item.skipped ? ' · skipped' : ''}
      </div>
    </div>
  )
}

// The items that needed help — the part of a set worth a teacher's attention.
// `details` may be the JSON string as stored, or an already-parsed array.
export function DrillMisses({ details }) {
  const items = Array.isArray(details) ? details : parseDrillDetails(details)
  if (items.length === 0) return null
  const tricky = items.filter(i => i && !i.perfect)
  if (tricky.length === 0) return <div style={S.clean}>Every answer first time — nothing needed help.</div>
  return (
    <div style={S.list}>
      {tricky.map((item, k) => (
        item.kind === 'verb' ? <VerbMiss key={k} item={item} />
          : item.kind === 'choice' ? <ChoiceMiss key={k} item={item} />
            : <CaseMiss key={k} item={item} />
      ))}
    </div>
  )
}
